import type {
  WaClearEvent,
  WaCreateEvent,
  WaOptionsRequestEvent,
} from "@web.awesome.me/webawesome-pro";
import type WaCombobox from "@web.awesome.me/webawesome-pro/dist/components/combobox/combobox.js";
import htmx from "htmx.org";
import { html, nothing, type PropertyValues } from "lit";
import { repeat } from "lit/directives/repeat.js";
import { z } from "zod";
import { checkNotNil } from "../check-not-nil.js";
import { http } from "../http.js";
import { FormAssociationMixin } from "../mixins/form-associated-element.js";
import { stateProperty } from "../property.js";
import type { ParserType } from "../schemas/parser.js";
import { QueryTagsResponse } from "../schemas/responses/query-tags.js";
import { Tags as Model, Tag } from "../schemas/tag.js";
import { WmElement } from "./element.js";

type Tags = ParserType<typeof Tags>;
const Tags = stateProperty(Model.schema, { defaultValue: [] });

type SelectedTagIds = ParserType<typeof SelectedTagIds>;
const SelectedTagIds = stateProperty(z.array(z.string()), { defaultValue: [] });

class WmTagsCombobox extends FormAssociationMixin(WmElement) {
  static readonly TAG_NAME = "wm-tags-combobox";

  static properties = {
    ...super.properties,
    availableTags: Tags,
    selectedTagIds: SelectedTagIds,
  };

  declare availableTags: Tags;
  declare selectedTagIds: SelectedTagIds;

  constructor() {
    super();
    this.name = "tags";
  }

  protected override render() {
    return html`
      <wa-combobox
        label="Tags"
        placeholder="Type to filer..."
        max-options-visible="2"
        allow-create
        multiple
        server
        with-clear
        @input=${this.#handleInput}
        @wa-create=${this.#handleCreate}
        @wa-options-request=${this.#handleOptionsRequest}
        @wa-clear=${this.#handleClear}>
        ${this.#renderTags()}
      </wa-combobox>
      `;
  }

  protected override updated(props: PropertyValues<this>): void {
    super.updated(props);
    const combobox = this.#queryCombobox();
    combobox.updateComplete.then(() => {
      combobox.getTag = (option) => {
        const id = option.value;
        const tag = this.availableTags.find((t) => t.id === id);
        return html`${tag != null ? Tag.format(tag) : nothing}`;
      };
      this.#updateForm();
    });
  }

  #handleClear(_: WaClearEvent) {
    this.selectedTagIds = [];
  }

  #handleCreate(event: WaCreateEvent) {
    event.preventDefault();

    const { inputValue } = event.detail;
    htmx.ajax("GET", `/app/tags/new?name=${encodeURIComponent(inputValue)}`, {
      target: "body",
      push: "true",
    });
  }

  #handleInput(e: InputEvent) {
    e.stopPropagation();
    const combobox = this.#queryCombobox();
    // Cannot be a single string, as multiple is set on the component.
    // See: https://webawesome.com/docs/components/combobox/#attributes-and-properties
    // The values are the selected tag IDs directly.
    const value = combobox.value as string[] | null;
    this.selectedTagIds = value ?? [];
  }

  async #handleOptionsRequest(e: WaOptionsRequestEvent) {
    const { query, signal } = e.detail;
    const combobox = this.#queryCombobox();

    const url = new URL("/app/tags", window.location.origin);
    url.search = new URLSearchParams({
      query: `name contains "${query}"`,
    }).toString();

    try {
      // TODO: impose a limit on best match. Will require more sophisticated
      // indexing so that "best match" means something. For now it's just some
      // ILIKE bullshit.
      const response = await http(url, {
        signal,
        headers: {
          Accept: "application/json",
        },
      });
      const body = QueryTagsResponse.parse(await response.json());
      this.#updateTagsFromServer(body);
    } catch (err) {
      // TODO: provide a toast for this bitch.
      this.logger.error("error requesting tags", err);
    } finally {
      combobox.loading = false;
    }
  }

  #renderTags() {
    const renderTag = (t: Tag) =>
      html`<wa-option value=${t.id} ?selected=${this.selectedTagIds.includes(t.id)}>${t.name}</wa-option`;

    const sorted = this.availableTags.toSorted((l, r) =>
      l.name.localeCompare(r.name),
    );

    return html`
      ${repeat(sorted, (t) => t.id, renderTag)}
    `;
  }

  #queryCombobox(): WaCombobox {
    return checkNotNil(this.renderRoot.querySelector("wa-combobox"));
  }

  #updateForm() {
    const combobox = this.#queryCombobox();
    combobox.updateComplete.then(() => {
      // Update the form stuff. At this time, we don't update the payload
      // because we have no need to force a re-render with new options.
      // This is already handled by the combobox, we simply proxy the form
      // stuff.
      const value = combobox.value as string[] | null;
      if (value == null) {
        this.internals.setFormValue(null);
      } else if (this.name) {
        const formData = new FormData();
        for (const v of value) {
          formData.append(this.name, v);
        }
        this.internals.setFormValue(formData);
      }

      combobox.updateValidity();
      this.internals.setValidity(
        combobox.validity,
        combobox.validationMessage,
        combobox.validationTarget,
      );
    });
  }

  // TODO: this logic in the setter? Would require to provide a custom
  // "transform" or some sheeeeeeet.
  #updateTagsFromServer(response: QueryTagsResponse) {
    // Keep the currently selected tags and append the new ones.
    const newTags = this.availableTags.filter((t) =>
      this.selectedTagIds.includes(t.id),
    );
    // Append the tags from the response that aren't already known.
    for (const tag of response.tags) {
      if (!newTags.find((t) => t.id === tag.id)) {
        newTags.push(tag);
      }
    }

    this.availableTags = newTags;
  }
}

customElements.define(WmTagsCombobox.TAG_NAME, WmTagsCombobox);

declare global {
  interface HTMLElementTagNameMap {
    [WmTagsCombobox.TAG_NAME]: WmTagsCombobox;
  }
}
